<!-- src/components/CustomConfirmModal.vue -->
<template>
  <div class="custom-message-wrapper">
    <transition-group name="message-modal-fade" tag="div">
      <div
          v-for="confirm in confirms"
          :key="confirm.id"
          class="custom-message-overlay"
      >
        <div class="custom-message-modal">
          <button
              class="custom-message-modal-close"
              @click="closeConfirm(confirm.id)"
          >
            <span class="custom-message-modal-close-x"></span>
          </button>

          <div class="custom-message-modal-content">
            <!-- 图标区域 -->
            <div class="custom-message-icon-container">
              <div :class="['custom-message-icon-outer', getIconClass(confirm.iconType)]">
                <i :class="['custom-message-icon', getIconTypeClass(confirm.iconType)]"></i>
              </div>
            </div>


            <!-- 内容区域 -->
            <div class="custom-message-content" v-html="formatContent(confirm.content || confirm.title)"></div>

            <!-- 按钮区域 -->
            <div class="custom-message-footer custom-confirm-footer">
              <button
                  v-if="!confirm.okOnly"
                  class="custom-message-button custom-message-button-cancel"
                  @click="handleCancel(confirm.id)"
              >
                {{ confirm.cancelText || 'Cancel' }}
              </button>
              <button
                  :class="['custom-message-button', getOkButtonClass(confirm.okType)]"
                  @click="handleOk(confirm.id)"
              >
                {{ confirm.okText || 'OK' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script>
export default {
  name: 'CustomConfirmModal',
  data() {
    return {
      confirms: [],
      seed: 0
    }
  },
  methods: {
    // 与 this.$customConfirm 保持一致的接口
    confirm(options) {
      const id = `confirm_${this.seed++}`;
      const confirm = {
        id,
        title: options.title || 'Confirm',
        content: options.content || '',
        okText: options.okText || this.getDefaultOkText(),
        cancelText: options.cancelText || this.getDefaultCancelText(),
        okType: options.okType || 'primary', // 默认为 primary
        okOnly: options.okOnly || false,
        iconType: options.iconType || 'confirm',
        onOk: options.onOk || (() => {}),
        onCancel: options.onCancel || (() => {})
      };

      this.confirms.push(confirm);
    },

    handleOk(id) {
      const confirm = this.confirms.find(c => c.id === id);
      if (confirm) {
        try {
          const result = confirm.onOk();
          if (result && typeof result.then === 'function') {
            // 如果返回Promise
            result.then(() => {
              this.closeConfirm(id);
            }).catch(() => {
              // 处理Promise拒绝情况
              this.closeConfirm(id);
            });
          } else {
            this.closeConfirm(id);
          }
        } catch (e) {
          console.error(e);
          this.closeConfirm(id);
        }
      }
    },

    handleCancel(id) {
      const confirm = this.confirms.find(c => c.id === id);
      if (confirm) {
        try {
          confirm.onCancel();
        } catch (e) {
          console.error(e);
        }
        this.closeConfirm(id);
      }
    },

    closeConfirm(id) {
      const index = this.confirms.findIndex(c => c.id === id);
      if (index !== -1) {
        this.confirms.splice(index, 1);
      }
    },

    // 格式化内容，支持换行符和制表符
    formatContent(content) {
      if (!content) return '';

      // 如果内容已经是HTML（包含HTML标签），则直接返回
      if (typeof content === 'string' && /<[a-z][\s\S]*>/i.test(content)) {
        return content;
      }

      // 处理纯文本内容，转换换行符和制表符
      return content
          .toString()
          .replace(/\n/g, '<br>')
          .replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;');
    },

    // 获取图标外框类名
    getIconClass(type) {
      const iconMap = {
        confirm: 'custom-message-icon-outer-confirm',
        info: 'custom-message-icon-outer-info',
        success: 'custom-message-icon-outer-success',
        warning: 'custom-message-icon-outer-warning',
        error: 'custom-message-icon-outer-error',
        danger: 'custom-message-icon-outer-danger'
      };
      return iconMap[type] || iconMap.confirm;
    },

    // 获取图标类型类名
    getIconTypeClass(type) {
      const iconMap = {
        confirm: 'custom-message-icon-confirm',
        info: 'custom-message-icon-info',
        success: 'custom-message-icon-success',
        warning: 'custom-message-icon-warning',
        error: 'custom-message-icon-error',
        danger: 'custom-message-icon-danger'
      };
      return `custom-message-icon ${iconMap[type] || iconMap.confirm}`;
    },

    // 获取确认按钮类名
    getOkButtonClass(type) {
      // 根据 okType 返回对应的按钮样式类
      const buttonMap = {
        primary: 'custom-message-button-confirm',  // 蓝色主按钮
        danger: 'custom-message-button-danger',    // 红色危险按钮
        dashed: 'custom-message-button-confirm',   // 虚线按钮（简化处理）
        ghost: 'custom-message-button-confirm',    // 透明按钮（简化处理）
        default: 'custom-message-button-confirm'   // 默认按钮
      };
      return buttonMap[type] || buttonMap.primary;
    },

    // 获取默认确认按钮文本
    getDefaultOkText() {
      try {
        if (this.$t && typeof this.$t === 'function') {
          return this.$t('publicLang.Confirm');
        }
      } catch (e) {
        console.warn('Failed to get default OK text translation', e);
      }
      return 'Confirm'; // 默认回退文本
    },

    // 获取默认取消按钮文本
    getDefaultCancelText() {
      try {
        if (this.$t && typeof this.$t === 'function') {
          return this.$t('publicLang.Cancel');
        }
      } catch (e) {
        console.warn('Failed to get default Cancel text translation', e);
      }
      return 'Cancel'; // 默认回退文本
    }
  }
}
</script>



<style scoped>
.custom-message-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
  pointer-events: none;
}

.custom-message-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  pointer-events: auto;
  z-index: 10000;
  padding-bottom: 60vh;
}

.custom-message-modal {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  width: 416px;
  max-width: calc(100vw - 32px);
  pointer-events: auto;
  position: relative;
  margin: 0 20px;
}

.custom-message-modal-close {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 10;
  padding: 0;
  color: rgba(0, 0, 0, 0.45);
  font-weight: 700;
  line-height: 1;
  text-decoration: none;
  background: transparent;
  border: 0;
  outline: 0;
  cursor: pointer;
  transition: color 0.3s;
  width: 56px;
  height: 56px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.custom-message-modal-close:hover {
  color: rgba(0, 0, 0, 0.7);
}

.custom-message-modal-close-x {
  display: block;
  width: 14px;
  height: 14px;
  position: relative;
}

.custom-message-modal-close-x::before,
.custom-message-modal-close-x::after {
  content: '';
  position: absolute;
  top: 7px;
  left: 0;
  width: 14px;
  height: 1px;
  background-color: rgba(0, 0, 0, 0.45);
  transition: background-color 0.3s;
}

.custom-message-modal-close-x::before {
  transform: rotate(45deg);
}

.custom-message-modal-close-x::after {
  transform: rotate(-45deg);
}

.custom-message-modal-close:hover .custom-message-modal-close-x::before,
.custom-message-modal-close:hover .custom-message-modal-close-x::after {
  background-color: rgba(0, 0, 0, 0.7);
}

.custom-message-modal-content {
  padding: 24px;
  text-align: center;
}

.custom-message-icon-container {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.custom-message-icon-outer {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 24px;
  border: 2px solid;
}

.custom-message-icon-outer-confirm {
  border-color: #faad14;
  color: #faad14;
}

.custom-message-icon-outer-info {
  border-color: #1890ff;
  color: #1890ff;
}

.custom-message-icon-outer-success {
  border-color: #52c41a;
  color: #52c41a;
}

.custom-message-icon-outer-warning {
  border-color: #faad14;
  color: #faad14;
}

.custom-message-icon-outer-error {
  border-color: #ff4d4f;
  color: #ff4d4f;
}

.custom-message-icon-outer-danger {
  border-color: #ff4d4f;
  color: #ff4d4f;
}

.custom-message-icon {
  font-weight: bold;
  width: 90%;
  height: 90%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 40px;
}

.custom-message-icon-confirm::before {
  content: "?";
}

.custom-message-icon-info::before {
  content: "i";
}

.custom-message-icon-success::before {
  content: "✓";
}

.custom-message-icon-warning::before {
  content: "!";
}

.custom-message-icon-error::before {
  content: "×";
}

.custom-message-icon-danger::before {
  content: "!";
}

.custom-message-content {
  font-size: 20px;
  text-align: center;
  margin-bottom: 24px;
  line-height: 1.5;
  word-wrap: break-word;
  white-space: pre-wrap;
  color: rgba(0, 0, 0, 0.85);
}

.custom-message-footer {
  display: flex;
  justify-content: center;
}

.custom-confirm-footer {
  justify-content: space-evenly;
}

.custom-message-button {
  padding: 0 15px;
  height: 32px;
  line-height: 30px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s;
  min-width: 80px;
}

.custom-message-button-confirm {
  color: #1890ff;
  background: #e6f7ff;
  border-color: #91d5ff;
}

.custom-message-button-confirm:hover {
  color: #40a9ff;
  border-color: #40a9ff;
}

.custom-message-button-cancel {
  color: rgba(0, 0, 0, 0.65);
  background: #fff;
  border-color: #d9d9d9;
}

.custom-message-button-cancel:hover {
  color: rgba(0, 0, 0, 0.85);
  border-color: #a6a6a6;
}

.custom-message-button-danger {
  color: #ff4d4f;
  background: #fff2f0;
  border-color: #ffccc7;
}

.custom-message-button-danger:hover {
  color: #ff7875;
  border-color: #ff7875;
}

.custom-message-button-info {
  color: #1890ff;
  background: #e6f7ff;
  border-color: #91d5ff;
}

.custom-message-button-info:hover {
  color: #40a9ff;
  border-color: #40a9ff;
}

.custom-message-button-success {
  color: #52c41a;
  background: #f6ffed;
  border-color: #b7eb8f;
}

.custom-message-button-success:hover {
  color: #73d13d;
  border-color: #73d13d;
}

.custom-message-button-warning {
  color: #faad14;
  background: #fffbe6;
  border-color: #ffe58f;
}

.custom-message-button-warning:hover {
  color: #ffc53d;
  border-color: #ffc53d;
}

.message-modal-fade-enter-active, .message-modal-fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.message-modal-fade-enter, .message-modal-fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.message-modal-fade-enter .custom-message-overlay, .message-modal-fade-leave-to .custom-message-overlay {
  opacity: 0;
}
</style>
